import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import type { GenericField } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { useSessionStore } from '@Stores';
import { useFormIntakeFeeFields } from '@IntakeModule';
import { useForm, useMFieldContact } from '@FormModule';
import { filterFieldsAccordingToTemplateHelper } from '../helpers';

const animalId = ref();
const animalStatuses = ref();
const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const firstAnimalGroupId = ref();
const formData = ref();
const formError = ref();
const formId = ref('');
const formList = ref();
const formName = ref('');
const formTemplateId = ref('');
const formTemplateName = ref('');
const formTitle = ref('');
const formType = ref('');
const initialFormTemplate = ref('null');
const intakeFormTitle = ref('');
const intakeFormType = ref('');
const isDefault = ref();
const isDuplicatedAnimal = ref(false);
const isGroupLinkedForm = ref(false);
const kennels = ref();
const rawFields = ref<RawField[]>([]);
const templateFields = ref();

export default function () {
  const { shelterProfile } = storeToRefs(useSessionStore());

  const { getDetailsSchema } = useForm();
  const { contactData, showCard } = useMFieldContact();

  const {
    intakeDateSettings,
    intakeFeeSettings,
    intakeWaiversSettings,
    needsWaiversSettings,
    onShowNeedsSignatureField,
  } = useFormIntakeFeeFields();

  const hasVisualKennel = computed(() => {
    if (!kennels.value) return;

    return kennels?.value?.find((kennel: any) => kennel?.name);
  });

  const fieldsSettings = ref<FieldInitialSettings>({
    intake_date: intakeDateSettings,
    intake_fee: intakeFeeSettings,
    intake_waivers: intakeWaiversSettings,
    needs_waivers: needsWaiversSettings,
    animal_name: {
      settings: {
        type: 'animal_name',
      },
    },
    kennel: {
      edit: {
        label: hasVisualKennel.value ? 'name' : '',
        options: kennels.value,
      },
      settings: {
        show: !shelterProfile.value?.use_kennel_manager,
      },
    },
    kennel_manager: {
      settings: {
        show: false,
      },
    },
    animal_status: {
      edit: {
        label: 'label',
      },
      settings: {
        required: true,
      },
    },
    coat_length: {
      edit: {
        label: 'value',
      },
    },
    age: {
      edit: {
        label: 'value',
      },
    },
    secondary_color_map: {
      edit: {
        label: 'value',
      },
    },
    tertiary_color_map: {
      edit: {
        label: 'value',
      },
    },
    birthday: {
      settings: {
        estBirthday: 'estimated',
        noModelOnStart: true,
      },
    },
    tags: {
      edit: {
        label: 'value',
      },
      settings: {
        type: 'multiselect',
      },
    },
    animal_template: {
      model: initialFormTemplate.value,
      settings: {
        show: true,
      },
    },
    breed: {
      edit: {
        options: [],
      },
      settings: {
        type: 'breeds',
      },
    },
    'google-address': {
      settings: {
        type: 'location',
      },
    },
    partner_name: {
      model: [],
      edit: {
        label: 'name',
        options: [],
      },
      settings: {},
    },
    photo: {
      settings: {
        type: 'file',
        multiple: true,
        fileLimit: 5,
      },
    },
    weight: {
      values: [],
    },
    file: {
      settings: {
        imageOnly: false,
        multiple: true,
        fileLimit: 5,
      },
    },
    secondary_breed: {
      edit: {
        options: [],
      },
      settings: {
        type: 'select',
      },
    },
    species: {
      model: [],
      edit: {
        options: [],
      },
      settings: {
        show: true,
      },
    },
    email: {
      settings: {
        type: 'contact',
      },
    },
    phone: {
      settings: {
        type: 'contact',
      },
    },
    microchip: {
      settings: {
        type: 'microchip',
      },
    },
    size: {
      edit: {
        label: 'value',
      },
    },
    group: {
      settings: {
        required: false,
        isDuplicatedAnimal: isDuplicatedAnimal.value,
        firstAnimalGroupId: firstAnimalGroupId.value,
        isGroupLinkedForm: isGroupLinkedForm.value,
      },
    },
  });

  const formTemplate = computed(() => selectedTemplate.value?.model);
  const needsSignatureModel = computed(() => fieldsSchema.value.find(({ name }) => name === 'needs_waivers')?.model);
  const selectedTemplate = computed(() => fieldsSchema.value.find(({ name }) => name === 'animal_template'));

  watch(contactData, () => updateSchemaModels(contactData.value));
  watch(isDuplicatedAnimal, () => updateGroupSettings());
  watch(needsSignatureModel, () => onShowNeedsSignatureField(fieldsSchema.value, needsSignatureModel.value));

  function updateGroupSettings() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'group') {
        field.settings = {
          ...field.settings,
          isDuplicatedAnimal: isDuplicatedAnimal.value,
          firstAnimalGroupId: firstAnimalGroupId.value,
          isGroupLinkedForm: isGroupLinkedForm.value,
        };
      }
    });

    return true;
  }

  function setFormAnimal(formFields: any, sourceData?: any, waivers?: any) {
    const updatedFieldSchema = getDetailsSchema(rawFields.value, fieldsSettings.value);
    fieldsSettings.value!.intake_waivers!.edit!.options = waivers;

    if (!sourceData) {
      fieldsSchema.value = updatedFieldSchema;

      filterFieldsAccordingToTemplateHelper(fieldsSchema, templateFields);
      updateGroupSettings();
      return;
    }

    updatedFieldSchema.forEach((field) => {
      const match = sourceData[field.name];
      if (['animal_template', 'animal_status', 'weight', 'needs_waivers', 'kennel'].includes(field.name)) return;

      switch (field.name) {
        case 'photo': {
          field.model = [];
          field.value = [];
          break;
        }
        case 'file': {
          field.model = [];
          field.value = [];
          break;
        }
        case 'size':
        case 'age':
        case 'coat_length':
        case 'secondary_color_map':
        case 'tertiary_color_map': {
          const fullField = formFields.find((f: any) => f.name === field.name);
          const option = fullField?.values?.find((v: any) => v.value === match);
          field.model = option || null;
          field.value = option || null;
          break;
        }
        default: {
          field.model = match;
          field.value = match;
        }
      }
    });

    fieldsSchema.value = updatedFieldSchema;
    filterFieldsAccordingToTemplateHelper(fieldsSchema, templateFields);
    updateGroupSettings();
  }

  function setFormPeople() {
    if (fieldsSchema.value.length) return;
    showCard.value = true;

    fieldsSchema.value = getDetailsSchema(rawFields.value, fieldsSettings.value);
  }

  function updateSchemaModels(sourceData: any) {
    setFormPeople();

    fieldsSchema.value.forEach((field) => {
      const match = sourceData[field.name];

      switch (field.name) {
        case 'country': {
          field.model = {
            country: sourceData.addresses[0]?.country,
            state: sourceData.addresses[0]?.state,
          };
          field.value = {
            country: sourceData.addresses[0]?.country,
            state: sourceData.addresses[0]?.state,
          };
          break;
        }
        case 'address1': {
          field.model = sourceData.addresses[0]?.address1;
          field.value = sourceData.addresses[0]?.address1;
          break;
        }
        case 'address2': {
          field.model = sourceData.addresses[0]?.address2;
          field.value = sourceData.addresses[0]?.address2;
          break;
        }
        case 'city': {
          field.model = sourceData.addresses[0]?.city;
          field.value = sourceData.addresses[0]?.city;
          break;
        }
        case 'zip': {
          field.model = sourceData.addresses[0]?.zip;
          field.value = sourceData.addresses[0]?.zip;
          break;
        }
        case 'phone': {
          const index = sourceData.matching_phone_index || 0;
          const phoneObj = sourceData.phones?.[index] ?? sourceData.matching_phone?.[0];
          const value = phoneObj?.phone ?? '';

          field.model = value;
          field.value = value;
          break;
        }
        case 'email': {
          const index = sourceData.matching_email_index || 0;
          const emailObj = sourceData.emails?.[index] ?? sourceData.matching_email?.[0];
          const value = emailObj?.email ?? '';

          field.model = value;
          field.value = value;
          break;
        }
        default: {
          field.model = match;
          field.value = match;
        }
      }

      showCard.value = false;
    });
  }

  function resetFieldSchema() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
    formData.value = null;
    formError.value = null;
    formId.value = '';
    formName.value = '';
    formTitle.value = '';
    rawFields.value = [];
    isDuplicatedAnimal.value = false;
    firstAnimalGroupId.value = null;
    isGroupLinkedForm.value = false;
  }

  return {
    animalId,
    animalStatuses,
    currentSchemaModels,
    fieldsSchema,
    fieldsSettings,
    firstAnimalGroupId,
    formData,
    formError,
    formId,
    formList,
    formName,
    formTemplate,
    formTemplateId,
    formTemplateName,
    formTitle,
    formType,
    initialFormTemplate,
    intakeFormTitle,
    intakeFormType,
    isDefault,
    isDuplicatedAnimal,
    isGroupLinkedForm,
    kennels,
    needsSignatureModel,
    rawFields,
    templateFields,

    resetFieldSchema,
    setFormAnimal,
    setFormPeople,
  };
}
